'use client'

import Link from 'next/link'
import { useEffect, useMemo, useRef, useState } from 'react'
import { cn } from '@/utilities/ui'
import { getTotalHeaderOffset } from '@/utilities/getTotalHeaderOffset'

export interface StickyAnchorItem {
  label: string
  href: string // e.g. #section-id
}

export interface StickyAnchorNavProps {
  items: StickyAnchorItem[]
}

export default function StickyAnchorNav({ items }: StickyAnchorNavProps) {
  const [active, setActive] = useState<string>(items[0]?.href ?? '')
  const ticking = useRef(false)
  const navRef = useRef<HTMLDivElement>(null)
  const scrollContainerRef = useRef<HTMLElement>(null)
  const linkRefs = useRef<Map<string, HTMLAnchorElement>>(new Map())
  const isUserScrollingRef = useRef(false)
  const userScrollTimeoutRef = useRef<number | null>(null)
  const prefersReduced =
    typeof window !== 'undefined' &&
    window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches

  const sectionIds = useMemo(
    () => items.map((i) => i.href.replace(/^#/, '')).filter(Boolean),
    [items],
  )

  // IntersectionObserver: nudges active on threshold crossings
  useEffect(() => {
    if (sectionIds.length === 0) return

    const elements = sectionIds
      .map((id) => document.getElementById(id))
      .filter((el): el is HTMLElement => Boolean(el))

    if (elements.length === 0) return

    const observer = new IntersectionObserver(
      (entries) => {
        const intersecting = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)
        const top = intersecting[0]
        if (top?.target?.id) {
          setActive(`#${top.target.id}`)
        }
      },
      {
        root: null,
        threshold: [0.1, 0.25, 0.5],
        rootMargin: '-10% 0px -60% 0px',
      },
    )

    elements.forEach((el) => observer.observe(el))
    return () => observer.disconnect()
  }, [sectionIds])

  // Scroll listener: computes current section based on a 35% viewport line
  useEffect(() => {
    if (sectionIds.length === 0) return

    const getCurrent = () => {
      const lineY = window.innerHeight * 0.35
      const candidates = sectionIds
        .map((id) => {
          const el = document.getElementById(id)
          if (!el) return null
          const rect = el.getBoundingClientRect()
          const inBand = rect.top <= lineY && rect.bottom >= lineY
          const distance = Math.abs(rect.top - lineY)
          return { id, inBand, distance }
        })
        .filter(Boolean) as { id: string; inBand: boolean; distance: number }[]

      const band = candidates.find((c) => c.inBand)
      if (band) return `#${band.id}`
      // Use reduce instead of sort for O(n) instead of O(n log n)
      const nearest = candidates.reduce<{ id: string; inBand: boolean; distance: number } | null>(
        (min, curr) => (!min || curr.distance < min.distance ? curr : min),
        null,
      )
      return nearest ? `#${nearest.id}` : active
    }

    const onScroll = () => {
      if (ticking.current) return
      ticking.current = true
      requestAnimationFrame(() => {
        const current = getCurrent()
        if (current && current !== active) setActive(current)
        ticking.current = false
      })
    }

    window.addEventListener('scroll', onScroll, { passive: true })
    // Initial calculation
    onScroll()
    return () => window.removeEventListener('scroll', onScroll)
  }, [sectionIds, active])

  // Calculate and set secondary header height as CSS variable
  useEffect(() => {
    const el = navRef.current
    if (!el) return

    const updateHeight = () => {
      const height = el.getBoundingClientRect().height
      try {
        document.documentElement.style.setProperty('--secondary-header-height', `${height}px`)
      } catch {}
    }

    updateHeight()
    window.addEventListener('resize', updateHeight, { passive: true })

    // Use ResizeObserver for dynamic content changes
    let ro: ResizeObserver | null = null
    try {
      ro = new ResizeObserver(updateHeight)
      ro.observe(el)
    } catch {
      // Fallback to window resize if ResizeObserver not available
    }

    return () => {
      window.removeEventListener('resize', updateHeight)
      if (ro) {
        ro.disconnect()
      }
    }
  }, [])

  // Auto-scroll the active item into horizontal view (mobile scrolling nav)
  useEffect(() => {
    if (isUserScrollingRef.current) return
    const container = scrollContainerRef.current
    const el = linkRefs.current.get(active)
    if (!container || !el) return
    if (container.scrollWidth <= container.clientWidth) return

    const containerRect = container.getBoundingClientRect()
    const elRect = el.getBoundingClientRect()
    const delta =
      elRect.left + elRect.width / 2 - (containerRect.left + container.clientWidth / 2)

    container.scrollBy({
      left: delta,
      behavior: prefersReduced ? 'auto' : 'smooth',
    })
  }, [active, prefersReduced])

  // Cleanup user-scroll timeout on unmount
  useEffect(() => {
    return () => {
      if (userScrollTimeoutRef.current !== null) {
        window.clearTimeout(userScrollTimeoutRef.current)
      }
    }
  }, [])

  const handleUserInteraction = () => {
    isUserScrollingRef.current = true
    if (userScrollTimeoutRef.current !== null) {
      window.clearTimeout(userScrollTimeoutRef.current)
    }
    userScrollTimeoutRef.current = window.setTimeout(() => {
      isUserScrollingRef.current = false
    }, 1000)
  }

  // Handle direct anchor navigation (URL with hash) on mount
  useEffect(() => {
    if (typeof window === 'undefined') return

    const handleAnchorNavigation = () => {
      const hash = window.location.hash
      if (!hash) return

      const targetId = hash.slice(1) // Remove the #
      const target = document.getElementById(targetId)
      if (!target) return

      // Wait a bit for layout to settle and CSS variables to be set
      const scrollToTarget = () => {
        const totalOffset = getTotalHeaderOffset()

        const lenis = (
          window as unknown as {
            __lenis?: {
              scroll?: (
                to: Element | number,
                opts?: { offset?: number; immediate?: boolean },
              ) => void
            }
          }
        ).__lenis

        if (lenis && typeof lenis.scroll === 'function') {
          lenis.scroll(target, { offset: -totalOffset, immediate: false })
        } else {
          // Fallback: calculate scroll position manually with offset
          const targetPosition = target.getBoundingClientRect().top + window.scrollY - totalOffset
          if (prefersReduced) {
            window.scrollTo({
              top: targetPosition,
              behavior: 'auto',
            })
          } else {
            window.scrollTo({
              top: targetPosition,
              behavior: 'smooth',
            })
          }
        }
      }

      // Use requestAnimationFrame to ensure DOM is ready
      requestAnimationFrame(() => {
        // Small delay to allow CSS variables to be set
        setTimeout(scrollToTarget, 100)
      })
    }

    handleAnchorNavigation()
  }, [prefersReduced])

  return (
    <div
      ref={navRef}
      className="sticky z-40 bg-background/90 backdrop-blur supports-[backdrop-filter]:bg-background/80 border-b border-border-brand/20"
      style={{ top: `var(--header-offset, var(--header-height, 0px))` }}
    >
      <div className="container mx-auto px-8 py-3">
        <nav
          ref={scrollContainerRef}
          onTouchMove={handleUserInteraction}
          className="flex gap-4 overflow-x-auto whitespace-nowrap [scrollbar-width:none] [&::-webkit-scrollbar]:hidden [mask-image:linear-gradient(to_right,black_85%,transparent)] [-webkit-mask-image:linear-gradient(to_right,black_85%,transparent)] md:flex-wrap md:overflow-visible md:[mask-image:none] md:[-webkit-mask-image:none]"
          aria-label="Produktbereiche"
        >
          {items.map((item) => {
            const isActive = active === item.href
            return (
              <Link
                key={item.href}
                ref={(el) => {
                  if (el) linkRefs.current.set(item.href, el)
                  else linkRefs.current.delete(item.href)
                }}
                href={item.href}
                scroll={false}
                onClick={(e) => {
                  // Smoothly scroll to section if available
                  const hash = item.href.startsWith('#') ? item.href.slice(1) : ''
                  if (!hash) return
                  const target = document.getElementById(hash)
                  if (!target) return

                  e.preventDefault()

                  if (prefersReduced) {
                    // Calculate scroll position manually with offset for reduced motion
                    const totalOffset = getTotalHeaderOffset()
                    const targetPosition =
                      target.getBoundingClientRect().top + window.scrollY - totalOffset
                    window.scrollTo({
                      top: targetPosition,
                      behavior: 'auto',
                    })
                    try {
                      history.pushState(null, '', `#${hash}`)
                    } catch {}
                    return
                  }

                  const lenis = (
                    window as unknown as {
                      __lenis?: {
                        scroll?: (
                          to: Element | number,
                          opts?: { offset?: number; immediate?: boolean },
                        ) => void
                      }
                    }
                  ).__lenis
                  if (lenis && typeof lenis.scroll === 'function') {
                    const totalOffset = getTotalHeaderOffset()
                    lenis.scroll(target, { offset: -totalOffset, immediate: false })
                  } else {
                    // Fallback: calculate scroll position manually with offset
                    const totalOffset = getTotalHeaderOffset()
                    const targetPosition =
                      target.getBoundingClientRect().top + window.scrollY - totalOffset
                    window.scrollTo({
                      top: targetPosition,
                      behavior: 'smooth',
                    })
                  }

                  // Update address bar hash without default jump
                  try {
                    history.pushState(null, '', `#${hash}`)
                  } catch {}
                }}
                className={cn(
                  'shrink-0 px-0 py-2 border-b transition-colors uppercase',
                  isActive
                    ? 'text-text-primary border-text-primary'
                    : 'text-text-primary/60 border-transparent hover:text-text-primary hover:border-text-primary',
                )}
              >
                {item.label}
              </Link>
            )
          })}
        </nav>
      </div>
    </div>
  )
}
